var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { WorkflowKind } from '@augloop/interfaces';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
var WorkflowGraph = /** @class */ (function () {
    function WorkflowGraph() {
        this.graphNodeByWorkflowId = new Map();
    }
    WorkflowGraph.prototype.addWorkflow = function (workflow) {
        if (this.graphNodeByWorkflowId.has(workflow.id)) {
            var logOp = new LogOperation({ resultDescription: "Adding a new with workflow with a duplicated workflowId: ".concat(workflow.id) });
            Log.error(0x1f403156 /* tag_5qdfw */, LogCategory.CoreDefault, logOp.stop());
            return;
        }
        var workflowGraphNode = new GraphNode(workflow);
        this.addWorkflowAsDependency(workflowGraphNode);
        this.graphNodeByWorkflowId.set(workflow.id, workflowGraphNode);
    };
    WorkflowGraph.prototype.getUpstreamRuntimeVisibleWorkflows = function () {
        var e_1, _a, _b;
        /**
         * We consider the following factors when building conceptual workflow compressions:
         *
         *  - All of them in Default visibility (items cross instance boundaries).
         *  - The ones with no input types are not added to the final list.
         *  - The ones with no output type are valid and are on the final list (only LocalOnly chain).
         *  - LocalOnly get output types from downstream workflows.
         */
        var externalWorkflows = [];
        try {
            for (var _c = __values(this.graphNodeByWorkflowId.values()), _d = _c.next(); !_d.done; _d = _c.next()) {
                var node = _d.value;
                var virtualWorkflow = this.createWorkflowDefinition(node.workflow);
                if (node.workflow.visibility === 1 /* LocalOnly */) {
                    // Compress downstream workflows LocalOnly output types
                    this.compressDownstreamWorkflows(virtualWorkflow, node);
                }
                else {
                    (_b = virtualWorkflow.outputTypes).push.apply(_b, __spreadArray([], __read(node.workflow.outputTypes), false));
                }
                if (virtualWorkflow.inputTypes.length !== 0) {
                    externalWorkflows.push(virtualWorkflow);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return externalWorkflows;
    };
    WorkflowGraph.prototype.getWorkflowsDefinitions = function () {
        return Array.from(this.graphNodeByWorkflowId.values()).map(function (node) { return node.workflow; });
    };
    WorkflowGraph.prototype.createWorkflowDefinition = function (workflow) {
        var virtualWorkflow = {
            id: "".concat(workflow.id, "-Virtual"),
            kind: workflow.kind,
            visibility: 0 /* Default */,
            inputTypes: workflow.inputTypes,
            outputTypes: [],
            collectionScopeType: workflow.collectionScopeType,
            correlatedSignals: workflow.correlatedSignals
        };
        return virtualWorkflow;
    };
    WorkflowGraph.prototype.compressDownstreamWorkflows = function (vw, workflowNode) {
        var e_2, _a, e_3, _b;
        try {
            for (var _c = __values(workflowNode.downstreamWorkflows), _d = _c.next(); !_d.done; _d = _c.next()) {
                var downstreamWorkflow = _d.value;
                if (downstreamWorkflow.workflow.visibility === 1 /* LocalOnly */) {
                    this.compressDownstreamWorkflows(vw, downstreamWorkflow);
                    continue;
                }
                if (downstreamWorkflow.workflow.kind === WorkflowKind.Join && vw.inputTypes.indexOf(downstreamWorkflow.workflow.collectionScopeType) === -1) {
                    continue;
                }
                try {
                    for (var _e = (e_3 = void 0, __values(downstreamWorkflow.workflow.outputTypes)), _f = _e.next(); !_f.done; _f = _e.next()) {
                        var outputType = _f.value;
                        if (vw.outputTypes.indexOf(outputType) === -1) {
                            vw.outputTypes.push(outputType);
                        }
                    }
                }
                catch (e_3_1) { e_3 = { error: e_3_1 }; }
                finally {
                    try {
                        if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                    }
                    finally { if (e_3) throw e_3.error; }
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_2) throw e_2.error; }
        }
    };
    WorkflowGraph.prototype.addWorkflowAsDependency = function (graphNode) {
        var e_4, _a, e_5, _b, e_6, _c;
        try {
            for (var _d = __values(this.graphNodeByWorkflowId.values()), _e = _d.next(); !_e.done; _e = _d.next()) {
                var workflowNode = _e.value;
                try {
                    for (var _f = (e_5 = void 0, __values(workflowNode.workflow.inputTypes)), _g = _f.next(); !_g.done; _g = _f.next()) {
                        var inputType = _g.value;
                        if (graphNode.workflow.outputTypes.indexOf(inputType) !== -1) {
                            this.addWorkflowChain(graphNode, workflowNode);
                        }
                    }
                }
                catch (e_5_1) { e_5 = { error: e_5_1 }; }
                finally {
                    try {
                        if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
                    }
                    finally { if (e_5) throw e_5.error; }
                }
                try {
                    for (var _h = (e_6 = void 0, __values(workflowNode.workflow.outputTypes)), _j = _h.next(); !_j.done; _j = _h.next()) {
                        var outputType = _j.value;
                        if (graphNode.workflow.inputTypes.indexOf(outputType) !== -1) {
                            this.addWorkflowChain(workflowNode, graphNode);
                        }
                    }
                }
                catch (e_6_1) { e_6 = { error: e_6_1 }; }
                finally {
                    try {
                        if (_j && !_j.done && (_c = _h.return)) _c.call(_h);
                    }
                    finally { if (e_6) throw e_6.error; }
                }
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_4) throw e_4.error; }
        }
    };
    WorkflowGraph.prototype.addWorkflowChain = function (upstreamWorkflow, downstreamWorkflow) {
        upstreamWorkflow.downstreamWorkflows.add(downstreamWorkflow);
        downstreamWorkflow.upstreamWorkflows.add(upstreamWorkflow);
    };
    return WorkflowGraph;
}());
export { WorkflowGraph };
var GraphNode = /** @class */ (function () {
    function GraphNode(workflow) {
        this.upstreamWorkflows = new Set();
        this.downstreamWorkflows = new Set();
        this.workflow = __assign(__assign({}, workflow), { inputTypes: !Array.isArray(workflow.inputTypes) ? [] : workflow.inputTypes, outputTypes: !Array.isArray(workflow.outputTypes) ? [] : workflow.outputTypes });
    }
    return GraphNode;
}());
//# sourceMappingURL=workflow-graph.js.map